import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { breadcrumbName } from '@/helpers/Utility';
import { ReadEmployee, useGetStaffByIdQuery } from '@/services/gql/graphql.generated';
import { format } from 'date-fns';
import { FC, Suspense, useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useParams } from 'react-router-dom';
import dayStrings from '../../team.translations';
import { TeamMemberHeader } from '../TeamMemberHeader';
import { TeamMemberProvider } from '../TeamMemberService';
import { TeamMemberCalendarDayViewAvailabilityCard } from './TeamMemberCalendarDayViewAvailabilityCard';
import { TeamMemberCalendarDayViewRosterCard } from './TeamMemberCalendarDayViewRosterCard';

export const TeamMemberCalendarDayView: FC = () => {
  const { userId } = useParams<'userId'>();
  const { date } = useParams<'date'>();

  if (!userId) throw Error('User id should be provided in url params');
  if (!date) throw Error('Date should be provided in url params');

  const dateObj = useMemo(() => new Date(date), [date]);

  const [{ data: userData, error: userError }] = useGetStaffByIdQuery({
    variables: { id: userId }
  });

  const handleError = useErrorHandler();
  if (userError) handleError(userError);

  const employee = userData?.employee as ReadEmployee | undefined;

  const breadcrumbs = [
    { name: dayStrings.teamPageTitle, to: '/team' },
    {
      name: employee ? breadcrumbName(employee, dayStrings.employeeName) : dayStrings.employeeName('', ''),
      to: `/team-member/${userId}`
    },
    {
      name: (
        <div className="flex items-center space-x-2">
          <AiOutlineCalendar />
          <p>{format(dateObj, 'EEEE do MMMM yyyy')}</p>
        </div>
      ),
      to: ''
    }
  ];

  return (
    <TeamMemberProvider>
      <PageContainer customHeader={<TeamMemberHeader employee={employee} breadcrumbs={breadcrumbs} />}>
        <Suspense fallback={<TableLoader />}>
          <div className="overflow-y-hidden flex flex-col bg-white">
            <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto space-y-6">
              <TeamMemberCalendarDayViewRosterCard dateObj={dateObj} userId={userId} />
              <TeamMemberCalendarDayViewAvailabilityCard dateObj={dateObj} date={date} userId={userId} />
            </PageSizer>
          </div>
        </Suspense>
      </PageContainer>
    </TeamMemberProvider>
  );
};
